import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
  type SliceCaseReducers,
} from '@reduxjs/toolkit';
import { CreateOrgUserData, createSqspOrgUser, fetchOrgUserData, listInvitedOrgUsers } from '../../apis/sqspUsers';
import {
  NOT_AUTHORIZED_USER_FETCH,
  NOT_AUTHORIZED_USER_INVITE,
  NOT_AUTHORIZED_USER_VIEW,
  SUCCESS_INVITATION,
} from '../../const/messageCodes';
import { PAGINATION_LIMIT_MIN } from '../../const/pagination';
import { PageInfo, PageInfoQueryWithSearch } from '../../models/PaginatedResponse';
import { OrgUser } from '../../models/SqspUser';
import { reduxErrorHandler } from '../../utils/reduxError';
import { sendSuccessMessageWithCode } from '../messages';
import { RootState } from '../rootReducer';
import { AppThunk } from '../store';

export type InvitedOrgUsersState = {
  isLoading: boolean;
  error: null | string | Record<string, string>;
  pageInfo: PageInfo;
} & EntityState<OrgUser>;

const invitedOrgUsersAdaptor = createEntityAdapter<OrgUser>();

export const invitedOrgUsersSlice = createSlice<
  InvitedOrgUsersState,
  SliceCaseReducers<InvitedOrgUsersState>,
  'invitedOrgUsers'
>({
  name: 'invitedOrgUsers',
  initialState: invitedOrgUsersAdaptor.getInitialState({
    isLoading: true,
    error: null,
    pageInfo: {
      total: 0,
      limit: PAGINATION_LIMIT_MIN,
      offset: 0,
    },
  }),
  reducers: {
    invitedOrgUserRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    invitedOrgUsersRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    invitedOrgUserReceived(state, action: PayloadAction<OrgUser>) {
      invitedOrgUsersAdaptor.upsertOne(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    invitedOrgUsersReceived(state, action: PayloadAction<OrgUser[]>) {
      invitedOrgUsersAdaptor.setAll(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    errorReceived(state, action: PayloadAction<string | Record<string, string>>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    updatePageInfo(state, action: PayloadAction<PageInfo>) {
      state.pageInfo = action.payload;
      state.isLoading = false;
    },
  },
});

// Selectors
export const { selectAll, selectById, selectTotal } = invitedOrgUsersAdaptor.getSelectors();

export const filterSingleInvitedUser = (state: RootState, id: number) => {
  const foundUser = selectAll(state.invitedOrgUsers).filter((invitedOrgUser: OrgUser) => invitedOrgUser.id === id);
  if (foundUser.length) {
    return foundUser[0];
  }
  return null;
};

export const { reducer: invitedOrgUsersReducer } = invitedOrgUsersSlice;

export const {
  invitedOrgUserRequested,
  invitedOrgUsersRequested,
  invitedOrgUserReceived,
  invitedOrgUsersReceived,
  errorReceived,
  updatePageInfo,
} = invitedOrgUsersSlice.actions;

export const fetchInvitedOrgUsers = (enterpriseId: number, options?: PageInfoQueryWithSearch): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(invitedOrgUsersRequested(undefined));
      const orgUsers = await listInvitedOrgUsers(enterpriseId, options);
      dispatch(invitedOrgUsersReceived(orgUsers.data));
      dispatch(updatePageInfo(orgUsers.pageInfo));
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_USER_VIEW);
    }
  };
};

export const createInvitedOrgUser = (enterpriseId: number, userData: CreateOrgUserData): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(invitedOrgUserRequested(undefined));
      const orgUser = await createSqspOrgUser(enterpriseId, userData);
      dispatch(sendSuccessMessageWithCode(SUCCESS_INVITATION, { user: userData.name }));
      dispatch(invitedOrgUserReceived(orgUser));
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_USER_INVITE);
    }
  };
};

export const fetchInvitedOrgUser = (enterpriseId: number, userId: number): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(invitedOrgUserRequested(undefined));
      const orgUser = await fetchOrgUserData(enterpriseId, userId);
      dispatch(invitedOrgUserReceived(orgUser));
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_USER_FETCH);
    }
  };
};
